import { Component } from '@theme/component';

/**
 * Truncates rich text to a set number of lines with a "View all" / "View less"
 * toggle. The toggle only shows up if the content actually overflows.
 *
 * @extends {Component<{content: HTMLElement, toggle: HTMLButtonElement}>}
 */
export class ExpandableTextComponent extends Component {
  requiredRefs = ['content', 'toggle'];

  connectedCallback() {
    super.connectedCallback();

    // Reading scrollHeight/clientHeight here forces the browser to flush
    // any pending layout immediately - during initial page parse, that's a
    // forced synchronous layout on the critical path. Defer one frame so it
    // reads after layout has settled naturally instead of causing it.
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#checkOverflow();
    });

    this.resizeObserver = new ResizeObserver(() => this.#checkOverflow());
    this.resizeObserver.observe(this.refs.content);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /** @type {number | null} */
  #rafId = null;

  #checkOverflow() {
    if (this.hasAttribute('expanded')) return;

    const { content, toggle } = this.refs;
    const isOverflowing = content.scrollHeight > content.clientHeight + 1;
    toggle.hidden = !isOverflowing;
  }

  toggle() {
    const expanded = this.toggleAttribute('expanded');
    const { toggle } = this.refs;

    toggle.textContent = expanded ? this.dataset.viewLessLabel : this.dataset.viewAllLabel;

    if (!expanded) this.#checkOverflow();
  }
}

if (!customElements.get('expandable-text-component')) {
  customElements.define('expandable-text-component', ExpandableTextComponent);
}
